import { Injectable, signal, WritableSignal, inject } from '@angular/core';
import { ColorVariable } from '../../@vex/components/config-panel/color-variables';
import { Design, ServiceCompanyDesignService, SubDomainDesignService } from '@eadsysteme/portalsettingclient-v1';
import { ConfigService } from 'src/@vex/services/config.service';
import { Title } from '@angular/platform-browser';

@Injectable({
	providedIn: 'root',
})
export class DesignUtil {
	private readonly serviceCompanyDesignService = inject(ServiceCompanyDesignService);
	private readonly subDomainDesignService = inject(SubDomainDesignService);
	private readonly configService = inject(ConfigService);
	private readonly titleService = inject(Title);

	readonly urlPattern = /^[\w-]+\.[\w-]+\.$/;
	public currentDesign: WritableSignal<Design> = signal(undefined);

	public async usePortalSettings(currentArea: number) {
		await this.loadDesign(currentArea);
	}

	private async loadDesign(currentArea: number) {
		let primaryColor: string;
		let warnColor: string;
		let accentColor: string;
		let domain: string = globalThis?.location?.host ?? '';
		domain = domain.slice(0, domain.lastIndexOf('.')) + '.';
		if (domain.includes('localhost') || !this.urlPattern.test(domain)) {
			domain = 'arasys.';
		}
		this.subDomainDesignService.getDomainDesign(currentArea, domain).subscribe({
			next: (design: Design) => {
				this.currentDesign.set(design);
				this.setColors(design);
				this.setImages(design);
			},
			complete: () => {
				return { primaryColor, warnColor, accentColor };
			},
		});
	}

	private setColors(design: Design) {
		const colorVariable: ColorVariable = {
			default: '',
			accent: '',
			warn: '',
			danger: 'rgb(208,33,39)',
			contrast: '',
			light: '',
		};

		for (const attribute of design.attributes) {
			switch (attribute.name) {
				case 'primaryColor':
					colorVariable.default = attribute.value;
					break;
				case 'accentColor':
					colorVariable.accent = attribute.value;
					break;
				case 'warnColor':
					colorVariable.warn = attribute.value;
					break;
				case 'tabTitleAraWeb':
					this.titleService.setTitle(attribute.value);
					break;
			}
		}

		this.configService.changeColorByCustomer(colorVariable);
	}

	private setImages(design: Design) {
		for (const blobAttribute of design.blobAttributes) {
			const img = this.b64toBlob(blobAttribute.value, 'image/png');
			if (blobAttribute.name === 'logo') {
				this.configService.changeImage('');
				const reader = new FileReader();
				reader.readAsDataURL(img);
				reader.onload = () => {
					this.configService.changeImage(reader.result as string);
				};
			} else if (blobAttribute.name === 'icon') {
				this.configService.changeFavIcon('');
				const reader = new FileReader();
				reader.readAsDataURL(img);
				reader.onload = () => {
					this.configService.changeFavIcon(reader.result as string);
				};
			}
		}
	}

	private b64toBlob(b64Data: string, contentType: string, sliceSize: number = 512) {
		const byteCharacters = atob(b64Data);
		const byteArrays = [];

		for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
			const slice = byteCharacters.slice(offset, offset + sliceSize);

			const byteNumbers = new Array(slice.length);
			for (let i = 0; i < slice.length; i++) {
				byteNumbers[i] = slice.codePointAt(i);
			}

			const byteArray = new Uint8Array(byteNumbers);
			byteArrays.push(byteArray);
		}

		return new Blob(byteArrays, { type: contentType });
	}
}
